import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquarePlus } from "lucide-react";
import { buildWhatsAppUrl } from "@/lib/brand";

export default function FloatingInquiryButton({ onClick }) {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const onScroll = () => setShown(window.scrollY > 200);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const waUrl = buildWhatsAppUrl(
    "Hi Thecoventures team, I’d like to inquire about a vending machine."
  );

  return (
    <AnimatePresence>
      {shown && (
        <motion.div
          key="fab-stack"
          initial={{ opacity: 0, y: 30, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 30, scale: 0.9 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="fixed bottom-6 right-4 sm:bottom-7 sm:right-7 z-40 flex items-center gap-2.5"
        >
          {/* WhatsApp direct chat */}
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="floating-whatsapp-button"
            aria-label="Chat on WhatsApp"
            onClick={() => {
              try {
                if (typeof window.gtag_report_conversion === "function") {
                  window.gtag_report_conversion();
                }
              } catch (_) { /* ignore */ }
            }}
            className="group inline-flex items-center justify-center w-12 h-12 sm:w-13 sm:h-13 rounded-full bg-[#25D366] hover:bg-[#1ebe5d] text-white shadow-[0_10px_30px_rgba(37,211,102,0.45)] transition"
          >
            <svg viewBox="0 0 32 32" className="w-5 h-5" fill="currentColor" aria-hidden="true">
              <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.04 2.264v.114c-.015.99.402 1.977 1.118 2.917 1.32 1.736 2.812 3.226 4.652 4.218.487.246.998.46 1.518.65.728.246 1.39.215 1.92.215.574 0 1.46-.187 1.92-.46.56-.337.84-.99.99-1.59.044-.214.044-.387.044-.572 0-.214-.014-.387-.043-.586-.044-.215-1.318-.864-1.677-.864zM16.04 5.726c-5.74 0-10.404 4.663-10.404 10.404 0 1.875.504 3.717 1.46 5.34l-1.917 5.755 5.92-1.89a10.336 10.336 0 0 0 4.94 1.243c5.74 0 10.404-4.663 10.404-10.404S21.78 5.726 16.04 5.726zm0 18.953a8.494 8.494 0 0 1-4.337-1.182l-.31-.184-3.215 1.027 1.044-3.13-.2-.32a8.55 8.55 0 0 1-1.31-4.555c0-4.722 3.842-8.563 8.563-8.563s8.563 3.84 8.563 8.563-3.84 8.563-8.563 8.563z"/>
            </svg>
          </a>

          {/* Open inquiry modal */}
          <button
            type="button"
            data-testid="floating-inquiry-button"
            onClick={onClick}
            aria-label="Open inquiry form"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-full pl-4 pr-5 py-3.5 transition shadow-[0_10px_30px_rgba(16,185,129,0.45)] animate-soft-pulse"
          >
            <span className="w-7 h-7 rounded-full bg-white/15 flex items-center justify-center">
              <MessageSquarePlus className="w-4 h-4" />
            </span>
            <span className="hidden sm:inline">Get a Free Quote</span>
            <span className="sm:hidden">Inquire</span>
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** Hook: auto-open the modal on 15s inactivity OR scroll past 60% of the page. */
export function useAutoTriggerInquiry({ enabled = true, onTrigger } = {}) {
  const firedRef = useRef(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    const resetTimer = () => {
      if (firedRef.current) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        if (firedRef.current) return;
        firedRef.current = true;
        onTrigger?.("inactivity");
      }, 15000);
    };

    const onScroll = () => {
      if (firedRef.current) return;
      const doc = document.documentElement;
      const total = doc.scrollHeight || 1;
      // Treat the scroll position itself (not the bottom edge of the viewport)
      // as the progress signal — so we only fire when the *user* has scrolled
      // 85 % of the way down the page.
      const pct = window.scrollY / total;
      if (pct >= 0.85) {
        firedRef.current = true;
        if (timerRef.current) clearTimeout(timerRef.current);
        onTrigger?.("scroll-85");
      }
    };

    const activityEvents = ["mousemove", "keydown", "touchstart", "click"];
    activityEvents.forEach((e) => window.addEventListener(e, resetTimer, { passive: true }));
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("scroll", resetTimer, { passive: true });

    resetTimer();

    return () => {
      activityEvents.forEach((e) => window.removeEventListener(e, resetTimer));
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("scroll", resetTimer);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [enabled, onTrigger]);
}
